import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { alpha } from "@mui/material/styles";
import Eyebrow from "@/components/common/Eyebrow";
import { format, differenceInDays } from "date-fns";
import { Lock, ShieldCheck, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const ADDONS = [
  { id: "chef", label: "Private chef", price: 4500 },
  { id: "transfer", label: "Airport transfer", price: 3500 },
  { id: "spa", label: "In-villa spa (90 min)", price: 6000 },
];

const ACCEPTANCES = [
  { id: "terms", label: "Terms & Conditions" },
  { id: "privacy", label: "Privacy Policy" },
  { id: "cancel", label: "Cancellation Policy" },
  { id: "refund", label: "Refund Policy" },
  { id: "rules", label: "House Rules" },
];

const PAYMENTS = [
  { id: "card", label: "Credit / Debit Card" },
  { id: "upi", label: "UPI" },
  { id: "netbanking", label: "Net Banking" },
  { id: "gpay", label: "Google Pay" },
  { id: "razorpay", label: "Razorpay" },
];

export default function Booking() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [villa, setVilla] = useState(null);
  const [addons, setAddons] = useState([]);
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [accepted, setAccepted] = useState({});
  const [payment, setPayment] = useState("card");
  const [submitting, setSubmitting] = useState(false);

  const from = params.get("from") ? new Date(params.get("from")) : null;
  const to = params.get("to") ? new Date(params.get("to")) : null;
  const guests = parseInt(params.get("guests") || "2");

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?redirect=" + encodeURIComponent(window.location.pathname + window.location.search)); return; }
    api.get(`/villas/${id}`).then(r => setVilla(r.data));
  }, [id, user, loading, nav]);

  if (loading) return <Box sx={{ py: 16, textAlign: "center", fontSize: "0.875rem", color: "text.secondary" }}>Loading…</Box>;
  if (!user || !villa || !from || !to) return <Box sx={{ py: 16, textAlign: "center" }}>Loading…</Box>;

  const nights = Math.max(1, differenceInDays(to, from));
  const basePrice = villa.discount_percent ? Math.round(villa.price_per_night * (1 - villa.discount_percent/100)) : villa.price_per_night;
  const subtotal = basePrice * nights;
  const addonTotal = addons.reduce((s, a) => s + ADDONS.find(x=>x.id===a).price, 0);
  const couponDiscount = appliedCoupon
    ? Math.round(appliedCoupon.discount_type === "percent" ? subtotal * appliedCoupon.amount / 100 : appliedCoupon.amount)
    : 0;
  const serviceFee = Math.round(subtotal * 0.08);
  const taxes = Math.round((subtotal + addonTotal - couponDiscount) * 0.18);
  const total = subtotal + addonTotal - couponDiscount + serviceFee + taxes;

  const allAccepted = ACCEPTANCES.every(a => accepted[a.id]);

  const applyCoupon = async () => {
    try {
      const { data } = await api.get("/coupons/validate", { params: { code: coupon, villa_id: villa.id } });
      setAppliedCoupon(data);
      toast(data.discount_type === "percent" ? `${data.amount}% off applied` : `₹${data.amount} off applied`);
    } catch (err) {
      setAppliedCoupon(null);
      toast(err.response?.data?.detail || "Invalid coupon code");
    }
  };

  const confirm = async () => {
    if (!allAccepted) { toast("Please accept all policies"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        villa_id: villa.id,
        check_in: from.toISOString(),
        check_out: to.toISOString(),
        guests, addons,
        coupon: appliedCoupon?.code ?? null,
        total,
      });
      toast("Booking confirmed — see you soon.");
      nav(`/dashboard?booking=${data.id}`);
    } catch { toast("Could not complete booking"); }
    finally { setSubmitting(false); }
  };

  return (
    <Box data-testid="booking-page" sx={{ maxWidth: 1300, mx: "auto", px: { xs: 3, lg: 6 }, py: 6 }}>
      <Eyebrow sx={{ mb: 1.5 }}>Secure checkout</Eyebrow>
      <Typography
        sx={{
          fontFamily: "'Playfair Display', serif",
          fontSize: { xs: "1.875rem", md: "2.25rem" },
          mb: 6,
        }}
      >
        Confirm your stay
      </Typography>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 440px" }, gap: 6 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <Box component="section">
            <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "1.5rem", mb: 2.5 }}>
              Your trip
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { sm: "repeat(3, 1fr)" }, gap: 2 }}>
              <Box sx={{ p: 2, borderRadius: 3, boxShadow: 1, border: 0 }}>
                <Eyebrow sx={{ fontSize: "9px", mb: 0.5 }}>Check-in</Eyebrow>
                <Typography sx={{ fontSize: "0.875rem" }}>{format(from, "EEE, dd MMM")}</Typography>
              </Box>
              <Box sx={{ p: 2, borderRadius: 3, boxShadow: 1, border: 0 }}>
                <Eyebrow sx={{ fontSize: "9px", mb: 0.5 }}>Check-out</Eyebrow>
                <Typography sx={{ fontSize: "0.875rem" }}>{format(to, "EEE, dd MMM")}</Typography>
              </Box>
              <Box sx={{ p: 2, borderRadius: 3, boxShadow: 1, border: 0 }}>
                <Eyebrow sx={{ fontSize: "9px", mb: 0.5 }}>Guests</Eyebrow>
                <Typography sx={{ fontSize: "0.875rem" }}>{guests}</Typography>
              </Box>
            </Box>
          </Box>

          <Box component="section">
            <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "1.5rem", mb: 2.5 }}>
              Add-ons
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {ADDONS.map(a => (
                <Box
                  key={a.id}
                  component="label"
                  data-testid={`addon-${a.id}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    borderRadius: 3,
                    boxShadow: 1,
                    border: 0,
                    cursor: "pointer",
                    transition: "box-shadow 0.2s",
                    "&:hover": { boxShadow: 3 },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Checkbox
                      checked={addons.includes(a.id)}
                      onChange={(e) => setAddons(p => e.target.checked ? [...p, a.id] : p.filter(x => x !== a.id))}
                      inputProps={{ "data-testid": `addon-${a.id}-input` }}
                      sx={{ p: 0 }}
                    />
                    <Typography sx={{ fontSize: "0.875rem" }}>{a.label}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>+₹{a.price.toLocaleString()}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box component="section">
            <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "1.5rem", mb: 2.5 }}>
              Payment method
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { sm: "1fr 1fr" }, gap: 1.5 }}>
              {PAYMENTS.map(p => (
                <Box
                  key={p.id}
                  component="button"
                  onClick={() => setPayment(p.id)}
                  data-testid={`pay-${p.id}`}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: payment === p.id ? "2px solid" : 0,
                    borderColor: payment === p.id ? "primary.main" : "transparent",
                    boxShadow: 1,
                    bgcolor: payment === p.id ? (t) => alpha(t.palette.primary.main, 0.05) : "background.paper",
                    textAlign: "left",
                    fontSize: "0.875rem",
                    fontFamily: "inherit",
                    color: "inherit",
                    cursor: "pointer",
                    transition: "box-shadow 0.2s, background-color 0.2s",
                    "&:hover": { boxShadow: 3 },
                  }}
                >
                  {payment === p.id && <Check size={12} style={{ display: "inline", marginRight: 8, verticalAlign: "-1px", color: "currentColor" }} />}
                  {p.label}
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1, fontSize: "0.75rem", color: "text.secondary" }}>
              <Lock size={12} /> Payments are encrypted and PCI-DSS compliant. <Box component="span" sx={{ color: "primary.main" }}>Test mode</Box>
            </Box>
          </Box>

          <Box component="section">
            <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "1.5rem", mb: 2.5 }}>
              Policies
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {ACCEPTANCES.map(a => (
                <Box
                  key={a.id}
                  component="label"
                  data-testid={`accept-${a.id}`}
                  sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer" }}
                >
                  <Checkbox
                    checked={!!accepted[a.id]}
                    onChange={(e) => setAccepted(p => ({ ...p, [a.id]: e.target.checked }))}
                    inputProps={{ "data-testid": `accept-${a.id}-input` }}
                    sx={{ p: 0 }}
                  />
                  <Typography sx={{ fontSize: "0.875rem" }}>
                    I accept the <Box component="span" sx={{ textDecoration: "underline" }}>{a.label}</Box>
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box component="aside">
          <Box
            sx={{
              position: { lg: "sticky" },
              top: { lg: 112 },
              borderRadius: 3.5,
              p: 3,
              boxShadow: 3,
              bgcolor: "background.paper",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, pb: 2.5, borderBottom: "1px solid", borderColor: "divider" }}>
              <Box
                component="img"
                src={villa.images[0]}
                alt=""
                sx={{ width: 96, height: 96, borderRadius: 3, objectFit: "cover" }}
              />
              <Box>
                <Eyebrow sx={{ fontSize: "9px" }}>{villa.location}</Eyebrow>
                <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "1.25rem", mt: 0.5 }}>
                  {villa.name}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.5 }}>
                  ★ {villa.rating} · {villa.reviews_count} reviews
                </Typography>
              </Box>
            </Box>

            <Box sx={{ py: 2.5, display: "flex", flexDirection: "column", gap: 1, fontSize: "0.875rem", borderBottom: "1px solid", borderColor: "divider" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <span>₹{basePrice.toLocaleString()} × {nights} nights</span>
                <span>₹{subtotal.toLocaleString()}</span>
              </Box>
              {addonTotal > 0 && (
                <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary" }}>
                  <span>Add-ons</span>
                  <span>₹{addonTotal.toLocaleString()}</span>
                </Box>
              )}
              {couponDiscount > 0 && (
                <Box sx={{ display: "flex", justifyContent: "space-between", color: "primary.main" }}>
                  <span>Coupon ({appliedCoupon?.code})</span>
                  <span>− ₹{couponDiscount.toLocaleString()}</span>
                </Box>
              )}
              <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary" }}>
                <span>Service fee</span>
                <span>₹{serviceFee.toLocaleString()}</span>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary" }}>
                <span>Taxes (18%)</span>
                <span>₹{taxes.toLocaleString()}</span>
              </Box>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "1rem", fontWeight: 500, py: 2.5, borderBottom: "1px solid", borderColor: "divider" }}>
              <span>Total</span>
              <span>₹{total.toLocaleString()}</span>
            </Box>

            <Box sx={{ display: "flex", gap: 1, py: 2.5 }}>
              <TextField
                value={coupon}
                onChange={e => setCoupon(e.target.value)}
                placeholder="Coupon code"
                size="small"
                fullWidth
                inputProps={{ "data-testid": "coupon-input" }}
                sx={{ flex: 1 }}
              />
              <Button onClick={applyCoupon} variant="outlined" data-testid="apply-coupon-btn" sx={{ whiteSpace: "nowrap" }}>
                Apply
              </Button>
            </Box>

            <Button
              onClick={confirm}
              disabled={!allAccepted || submitting}
              variant="contained"
              fullWidth
              data-testid="confirm-booking-btn"
            >
              {submitting ? "Confirming…" : "Confirm & Pay"}
            </Button>

            <Box sx={{ mt: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 1, fontSize: "0.75rem", color: "text.secondary" }}>
              <ShieldCheck size={12} /> Secure SSL · Tokenized payment
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
